import { defer, Observable, of } from "rxjs";
import * as Sentry from "@sentry/angular";
import { inject } from "@angular/core";
import { AuthService } from "@app/kernel/tools/services/auth.service";

/**
 * Initializes the Sentry user context synchronization with the application's authentication state.
 *
 * This initializer subscribes to the {@link AuthService.user$} observable
 * and updates Sentry's user context whenever the authenticated user changes.
 * This ensures that all captured Sentry events include the correct user information
 * (e.g., ID, username, and email).
 *
 * @returns An {@link Observable} that completes immediately after initialization.
 *
 * @example
 * ```ts
 * sentryUserContextInitializer().subscribe();
 * ```
 *
 * @remarks
 * - When no user is logged in, Sentry's user context is cleared (`Sentry.setUser(null)`).
 * - When a user logs in or updates, Sentry is updated with the latest `id`, `name`, and `email`.
 * - If Sentry is disabled, the initializer simply returns an empty observable.
 */
export function sentryUserContextInitializer(): Observable<unknown> {
  const authService = inject(AuthService);

  return defer(() => {
    if (!Sentry.isEnabled()) return of(void 0);

    // Keep Sentry's user context in sync with the authentication state.
    authService.user$.subscribe(user => {
      Sentry.setUser(user ? { id: user.id, username: user.name, email: user.email } : null);
    });

    return of(void 0);
  });
}
